import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { EndpointQueryData } from '../types';

type UpsertSmsOntoGetThreadQueryArgs = {
  query: EndpointQueryData<'GetThread'>;
  newSms: SMS;
  matchingSmsId: string;
};

/**
 * Updates a GetThread query's cached data by upserting a new SMS message into the appropriate position
 * within the thread's message list, maintaining chronological order.
 *
 * It takes in an object with the following properties:
 * @param query - A tuple containing the query key and query data for the GetThread endpoint
 * @param newSms - The new SMS message to be upserted into the thread
 * @param matchingSmsId - The ID of any existing SMS that should be replaced by the new SMS
 *
 * @returns A new EndpointQueryData tuple with updated pages containing the upserted SMS message
 */
export const upsertSmsOntoGetThreadQuery = ({
  query: [queryKey, queryData],
  newSms,
  matchingSmsId,
}: UpsertSmsOntoGetThreadQueryArgs): EndpointQueryData<'GetThread'> => {
  const request = queryKey[2];
  const hasLatestMessage =
    !request.filter?.focusCreatedAt || !request.filter.focusCreatedAt || queryData.pages[0]?.newerPageToken === '';
  const newSmsCreatedAt = new Date(newSms.createdAt).getTime();
  const hasNewerOrMatchingSms = queryData.pages.some(
    (page) =>
      page.thread.messages.some((sms) => new Date(sms.createdAt).getTime() > newSmsCreatedAt) ||
      page.thread.messages.some((sms) => sms.id === matchingSmsId)
  );
  const pagesMaxIndex = queryData.pages.length - 1;
  const lastPageMessagesMaxIndex = pagesMaxIndex >= 0 ? queryData.pages[pagesMaxIndex]!.thread.messages.length - 1 : -1;
  const lastMessageIsMatch =
    lastPageMessagesMaxIndex >= 0
      ? queryData.pages[pagesMaxIndex]!.thread.messages[lastPageMessagesMaxIndex]!.id === matchingSmsId
      : false;
  const { newPages } = queryData.pages.reduce<{
    hasUpserted: boolean;
    newPages: typeof queryData.pages;
  }>(
    (pagesAcc, page) => {
      const { newMessages, hasUpserted } = page.thread.messages.reduce<{
        hasUpserted: boolean;
        newMessages: typeof page.thread.messages;
      }>(
        (messagesAcc, sms) => {
          const smsCreatedAt = new Date(sms.createdAt).getTime();

          const mode = messagesAcc.hasUpserted
            ? 'filter'
            : hasLatestMessage || hasNewerOrMatchingSms
            ? 'upsert'
            : 'cannot-upsert';

          switch (mode) {
            case 'upsert': {
              if (smsCreatedAt < newSmsCreatedAt && sms.id !== matchingSmsId) {
                messagesAcc.newMessages.push(newSms);
                messagesAcc.newMessages.push(sms);
                messagesAcc.hasUpserted = true;
              } else if (lastMessageIsMatch && sms.id === matchingSmsId) {
                messagesAcc.newMessages.push(newSms);
              } else if (sms.id !== matchingSmsId) {
                messagesAcc.newMessages.push(sms);
              }
              break;
            }
            case 'filter': {
              if (sms.id !== matchingSmsId) {
                messagesAcc.newMessages.push(sms);
              }
              break;
            }
            case 'cannot-upsert': {
              messagesAcc.newMessages.push(sms);
              break;
            }
          }

          return messagesAcc;
        },
        {
          hasUpserted: pagesAcc.hasUpserted,
          newMessages: [],
        }
      );

      pagesAcc.hasUpserted = pagesAcc.hasUpserted || hasUpserted;
      pagesAcc.newPages.push({
        ...page,
        thread: {
          ...page.thread,
          messages: newMessages,
        },
      });

      return pagesAcc;
    },
    { hasUpserted: false, newPages: [] }
  );

  return [
    queryKey,
    {
      ...queryData,
      pages: newPages,
    },
  ];
};
